import { useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import ScrollToTopButton from '@/components/feature/ScrollToTopButton';
import MovieCard from '@/components/feature/MovieCard';
import { movies } from '@/mocks/content';
import type { ContentItem } from '@/types/content';
import { useSeo } from '@/hooks/useSeo';

const TYPE_TAGS = ['全部', '电影', '电视剧', '综艺'];
const YEAR_TAGS = ['全部', '2024', '2023', '2022'];

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const query = searchParams.get('q') ?? '';
  const [input, setInput] = useState(query);
  const [typeTag, setTypeTag] = useState('全部');
  const [yearTag, setYearTag] = useState('全部');

  useSeo(
    query ? `搜索“${query}” - 91大神影视大全` : '影视搜索 - 91大神影视大全',
    '在91大神影视大全中搜索电影、电视剧、综艺等影视资源，支持关键词与类型、年代筛选。',
  );

  const allItems = movies as ContentItem[];
  const keyword = query.trim();

  const results = useMemo(() => {
    let list = allItems;
    if (keyword) {
      const lower = keyword.toLowerCase();
      list = list.filter(
        (item) =>
          item.title.toLowerCase().includes(lower) ||
          item.genres.some((genre) => genre.toLowerCase().includes(lower)) ||
          item.actors.some((actor) => actor.toLowerCase().includes(lower)) ||
          item.director.toLowerCase().includes(lower),
      );
    }
    if (typeTag !== '全部') {
      list = list.filter((item) => item.type === typeTag);
    }
    if (yearTag !== '全部') {
      list = list.filter((item) => `${item.year}` === yearTag);
    }
    return list;
  }, [allItems, keyword, typeTag, yearTag]);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = input.trim();
    setSearchParams(value ? { q: value } : {});
  };

  const hotPicks = [...allItems].sort((a, b) => b.views - a.views).slice(0, 5);

  return (
    <div className="min-h-screen w-full bg-background-100 flex flex-col">
      <SiteHeader />

      <main className="w-full flex-1 px-4 md:px-8 py-6 md:py-8">
        <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-5">
          <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">
            首页
          </Link>
          <i className="ri-arrow-right-s-line text-base" />
          <span className="text-foreground-800">搜索</span>
        </nav>

        <h1 className="font-heading font-extrabold text-2xl md:text-3xl text-foreground-950">
          影视搜索
        </h1>

        <form
          onSubmit={handleSubmit}
          className="mt-5 flex items-center gap-3 max-w-2xl"
        >
          <div className="flex flex-1 items-center h-12 rounded-full bg-background-50 border border-background-200 focus-within:border-primary-400 transition-colors px-4">
            <i className="ri-search-line text-foreground-500 text-lg" />
            <input
              type="text"
              value={input}
              onChange={(event) => setInput(event.target.value)}
              placeholder="输入影视名称、类型或演员"
              className="flex-1 bg-transparent border-none outline-none text-sm text-foreground-900 placeholder:text-foreground-400 px-2"
            />
          </div>
          <button
            type="submit"
            className="h-12 px-6 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            搜索
          </button>
        </form>

        <div className="mt-6 space-y-3">
          <div className="flex items-start gap-3">
            <span className="w-12 shrink-0 pt-1 text-sm text-foreground-500">类型</span>
            <div className="flex flex-wrap gap-2">
              {TYPE_TAGS.map((tag) => (
                <button
                  key={tag}
                  type="button"
                  onClick={() => setTypeTag(tag)}
                  className={`px-3 py-1.5 rounded-md text-sm whitespace-nowrap transition-colors cursor-pointer ${
                    tag === typeTag
                      ? 'bg-primary-500 text-background-50 font-medium'
                      : 'bg-background-50 border border-background-200 text-foreground-700 hover:border-primary-300'
                  }`}
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-start gap-3">
            <span className="w-12 shrink-0 pt-1 text-sm text-foreground-500">年代</span>
            <div className="flex flex-wrap gap-2">
              {YEAR_TAGS.map((tag) => (
                <button
                  key={tag}
                  type="button"
                  onClick={() => setYearTag(tag)}
                  className={`px-3 py-1.5 rounded-md text-sm whitespace-nowrap transition-colors cursor-pointer ${
                    tag === yearTag
                      ? 'bg-primary-500 text-background-50 font-medium'
                      : 'bg-background-50 border border-background-200 text-foreground-700 hover:border-primary-300'
                  }`}
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-6 mb-4">
          {keyword ? (
            <p className="text-sm text-foreground-600">
              关键词“<span className="text-primary-600 font-medium">{keyword}</span>”，共找到{' '}
              <span className="text-accent-600 font-semibold">{results.length}</span> 个结果
            </p>
          ) : (
            <p className="text-sm text-foreground-600">
              输入关键词开始搜索，或浏览下方热门推荐。
            </p>
          )}
        </div>

        {keyword && results.length === 0 ? (
          <div className="py-16 flex flex-col items-center text-center">
            <span className="w-16 h-16 rounded-full bg-background-200 flex items-center justify-center text-foreground-400">
              <i className="ri-search-eye-line text-3xl" />
            </span>
            <h2 className="mt-5 font-heading font-semibold text-lg text-foreground-900">
              没有找到与“{keyword}”相关的内容
            </h2>
            <p className="mt-2 text-sm text-foreground-500 max-w-md">
              换个关键词试试，或看看下面这些热门影视。
            </p>
            <button
              type="button"
              onClick={() => {
                setInput('');
                setTypeTag('全部');
                setYearTag('全部');
                navigate('/search');
              }}
              className="mt-5 px-6 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              清空搜索条件
            </button>
          </div>
        ) : keyword && results.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-5">
            {results.map((item) => (
              <MovieCard key={item.id} item={item} />
            ))}
          </div>
        ) : null}

        {(!keyword || results.length === 0) && (
          <section className="mt-10">
            <div className="flex items-center gap-3 mb-5">
              <span className="w-1 h-6 rounded-full bg-accent-500" />
              <h2 className="font-heading font-bold text-xl text-foreground-950">
                热门推荐
              </h2>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-5">
              {hotPicks.map((item) => (
                <MovieCard key={item.id} item={item} />
              ))}
            </div>
          </section>
        )}
      </main>

      <SiteFooter />
      <ScrollToTopButton />
    </div>
  );
}